import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuthStore, useLangStore } from "@/store/useStore";
import { authApi } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  User, Lock, Loader2, MessageCircle, AlertTriangle,
  Languages, CheckCircle2, ArrowRight, Sparkles, Eye, EyeOff,
} from "lucide-react";
import { ChitLogo } from "@/components/ChitLogo";

const features = [
  {
    icon: MessageCircle,
    title_en: "WhatsApp reminders",
    title_te: "వాట్సాప్ రిమైండర్‌లు",
    desc_en: "Send polite payment nudges in one tap.",
    desc_te: "ఒక్క క్లిక్‌తో చెల్లింపు రిమైండర్ పంపండి.",
  },
  {
    icon: AlertTriangle,
    title_en: "Zero surprises",
    title_te: "ఏ ఆశ్చర్యాలు లేవు",
    desc_en: "See every pending payment across every group instantly.",
    desc_te: "ప్రతి గ్రూప్‌లో పెండింగ్ చెల్లింపులు వెంటనే చూడండి.",
  },
  {
    icon: Languages,
    title_en: "Telugu + English",
    title_te: "తెలుగు + ఇంగ్లిష్",
    desc_en: "Switch languages any time — whole app updates.",
    desc_te: "ఎప్పుడైనా భాష మారండి — యాప్ మొత్తం మారుతుంది.",
  },
];

export default function Login() {
  const { t } = useTranslation();
  const { setAuth } = useAuthStore();
  const { lang, setLang } = useLangStore();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error(t("login.error_empty"));
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.login(username.trim(), password);
      setAuth(res.data.token, res.data.user);
      if (res.data.user?.language && res.data.user.language !== lang) {
        setLang(res.data.user.language);
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("login.error_invalid"));
    } finally {
      setLoading(false);
    }
  };

  const isT = lang === "te";

  return (
    <div className={`min-h-screen flex flex-col lg:flex-row ${isT ? "font-telugu" : ""}`} lang={lang}>
      {/* ===== Hero / Brand panel (left on desktop, top on mobile) ===== */}
      <aside
        className="relative overflow-hidden bg-[#1A3C6E] text-white lg:w-[55%] px-6 sm:px-10 lg:px-14 py-10 lg:py-14"
        data-testid="home-hero"
      >
        {/* Decorative layers */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          {/* Purple glow blob top-right */}
          <div className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#6B3FA0]/40 blur-3xl" />
          {/* Soft amber blob bottom-left */}
          <div className="absolute -bottom-40 -left-20 h-[360px] w-[360px] rounded-full bg-[#F59E0B]/15 blur-3xl" />
          {/* Grid pattern overlay */}
          <svg className="absolute inset-0 w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="ct-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ct-grid)" />
          </svg>
          {/* Big ₹ watermark */}
          <div className="absolute right-6 bottom-6 text-[220px] leading-none font-black text-white/[0.04] select-none hidden lg:block">
            ₹
          </div>
        </div>

        <div className="relative z-10 h-full flex flex-col min-h-full">
          {/* Top row: logo + language toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ChitLogo tone="light" className="h-11 w-11 shrink-0" />
              <div className="text-xl font-bold tracking-tight">{t("common.app_name")}</div>
            </div>
            <div className="inline-flex rounded-full bg-white/10 border border-white/15 overflow-hidden text-xs backdrop-blur">
              <button
                data-testid="login-lang-en"
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 font-semibold transition-colors ${lang === "en" ? "bg-white text-[#1A3C6E]" : "text-white/80 hover:text-white"}`}
              >
                EN
              </button>
              <button
                data-testid="login-lang-te"
                onClick={() => setLang("te")}
                className={`px-3 py-1.5 font-semibold transition-colors ${lang === "te" ? "bg-white text-[#1A3C6E]" : "text-white/80 hover:text-white"}`}
              >
                తె
              </button>
            </div>
          </div>

          {/* Hero copy */}
          <div className="flex-1 flex flex-col justify-center py-10 lg:py-16 animate-fade-up">
            <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] font-semibold text-white/90 mb-5 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-[#F59E0B]" />
              {isT ? "నోట్‌బుక్‌ల యుగం ముగిసింది" : "The notebook era is over"}
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-[56px] font-bold tracking-tight leading-[1.05] mb-5 max-w-xl">
              {isT ? (
                <>
                  మీ చిట్‌ఫండ్<br />
                  <span className="text-[#F5D78E]">ఒకేచోట.</span>
                </>
              ) : (
                <>
                  Your family chits,<br />
                  <span className="text-[#F5D78E]">in one calm place.</span>
                </>
              )}
            </h1>
            <p className="text-base sm:text-lg text-white/75 leading-relaxed max-w-md mb-10">
              {isT
                ? "వాట్సాప్ మెసేజ్‌లు, నోట్‌బుక్‌లు అన్నీ వదిలేయండి. ఒక స్థలంలోనే చెల్లింపులు, బకాయిదారులు, రిమైండర్‌లు."
                : "Skip the scrolling WhatsApp threads and notebook columns. Track payments, surface pending dues, and send reminders — all from here."}
            </p>

            {/* Feature chips — 3 column grid on sm+, stacked on smallest */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={i}
                    className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm hover:bg-white/[0.08] transition-colors"
                    data-testid={`home-feature-${i}`}
                  >
                    <div className="h-9 w-9 rounded-lg bg-white/10 grid place-items-center mb-3">
                      <Icon className="h-4.5 w-4.5 text-[#F5D78E]" />
                    </div>
                    <div className="text-sm font-semibold leading-snug">
                      {isT ? f.title_te : f.title_en}
                    </div>
                    <div className="text-[11px] text-white/60 mt-1 leading-relaxed">
                      {isT ? f.desc_te : f.desc_en}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer sentiment row (desktop only) */}
          <div className="hidden lg:flex items-center gap-2 text-xs text-white/50 mt-8">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>
              {isT
                ? "టెలుగు + ఇంగ్లిష్ · WhatsApp రిమైండర్‌లు · కొన్ని నిమిషాల్లో సెటప్"
                : "Telugu + English · WhatsApp reminders · Set up in minutes"}
            </span>
          </div>
        </div>
      </aside>

      {/* ===== Login panel (right on desktop, below on mobile) ===== */}
      <main className="relative flex-1 lg:w-[45%] bg-[#F8F9FA] flex items-center justify-center px-5 sm:px-10 py-10 lg:py-14">
        <div className="w-full max-w-md animate-fade-up">
          <div className="rounded-3xl bg-white border border-gray-200 shadow-[0_20px_60px_-30px_rgba(13,27,42,0.2)] p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-2xl sm:text-[28px] font-bold text-[#0D1B2A] tracking-tight leading-tight">
                {t("login.title")}
              </h2>
              <p className="text-sm text-[#4B5563] mt-2">{t("login.subtitle")}</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-sm font-semibold text-[#0D1B2A]">{t("login.username_label")}</Label>
                <div className="relative mt-2">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6B7280]" />
                  <Input
                    data-testid="login-username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                    placeholder={t("login.username_placeholder")}
                    className="h-13 pl-12 text-base rounded-xl border-gray-300 focus-visible:ring-[#1A3C6E]"
                    autoComplete="username"
                    autoFocus
                    style={{ height: "52px" }}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-[#0D1B2A]">{t("login.password_label")}</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6B7280]" />
                  <Input
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("login.password_placeholder")}
                    className="h-13 pl-12 pr-12 text-base rounded-xl border-gray-300 focus-visible:ring-[#1A3C6E]"
                    autoComplete="current-password"
                    style={{ height: "52px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="login-password-toggle"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-lg text-[#6B7280] hover:text-[#1A3C6E] hover:bg-[#E8EDF4] transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="h-13 w-full rounded-xl bg-[#1A3C6E] hover:bg-[#122A4D] text-white text-base font-semibold group"
                style={{ height: "52px" }}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {t("login.sign_in")}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                )}
              </Button>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-[11px] text-[#9CA3AF] font-medium uppercase tracking-wider">
                  {isT ? "లేదా" : "or"}
                </span>
              </div>
            </div>

            <Link
              to="/signup"
              data-testid="login-signup-link"
              className="block w-full h-12 rounded-xl border border-gray-300 hover:border-[#1A3C6E] hover:bg-[#E8EDF4] transition-colors text-center leading-[3rem] text-sm font-semibold text-[#1A3C6E]"
            >
              {t("login.no_account")} <span className="underline">{t("login.sign_up")}</span>
            </Link>
          </div>

          <p className="text-center text-[11px] text-[#9CA3AF] mt-6">
            © ChitTrack
          </p>
        </div>
      </main>
    </div>
  );
}
